import React from "react";
import { ResponsiveTable } from "../ResponsiveTable";
import {
  Box,
  MenuItem,
  Select,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { MULISH } from "../../WireGuardTunnels/VpnDebugger/vpnDebugConstants";
import { useTroubleshootingStore } from "../troubleshootingStore/troubleshootingStore";
import { useAppStore } from "../../../../appStore";
import { TargetHostField, useTargetCheck } from "../TargetHostField";
import { tunnelMenuItems, tunnelsRouting } from "./tunnelMenu";
import { FAMILY_LABEL, siteFamily } from "../TroubleshootingUtils/siteFamily";
import { savedLoginSupported } from "../TroubleshootingUtils/savedLoginTest";
import { SavedLoginTest } from "./SavedLoginTest";
import {
  endpointProfile,
  primaryHost,
} from "../TroubleshootingUtils/integrationEndpoints";
import {
  camerasPerRecordingServer,
  isPrivateAddress,
  NetworkTarget,
  siteLocalTargets,
} from "../TroubleshootingUtils/probeTargets";
import {
  CREDENTIAL_CHECK_COOLDOWN_SECONDS,
  PORT_CHECK_COOLDOWN_SECONDS,
  S2S_CHECK_COOLDOWN_SECONDS,
  useCooldown,
  waitLabel,
} from "../TroubleshootingUtils/useCooldown";
import {
  buildCredentialPrefill,
  CameraLogin,
  sitePorts,
  CredentialPrefill,
} from "../TroubleshootingUtils/probePrefill";
import {
  SiteConnectionDetail,
  CredentialCheckResponse,
  GapResult,
  PortCheckResponse,
  ProbeOrigin,
  SiteToSiteCheckResult,
  SiteToSiteConnection,
  SiteToSiteRungId,
} from "../troubleshootingInterfaces";
import {
  Card,
  CardHeader,
  CELL_SX,
  EmptyCard,
  GapNotice,
  HEAD_CELL_SX,
  SHADED_HEAD_CELL_SX,
  Hint,
  KindChip,
  LadderRung,
  Mono,
  Pill,
  PORT_LABEL,
  PORT_TONE,
  SampleChip,
  StatusDot,
  TS_COLORS,
  TsButton,
  SiteLink,
} from "../TroubleshootingUi";
import {
  runCredentialCheck,
  runPortCheck,
  runSiteToSiteCheck,
  fetchCameraLogin,
} from "../TroubleshootingUtils/troubleshootingApi";
import {
  formatRelative,
  parsePortList,
} from "../TroubleshootingUtils/formatters";
import {
  connectionKindForSite,
  siteCameraHosts,
  useScopedSites,
  useScopedTunnels,
  useTabScope,
  useServerName,
} from "../TroubleshootingUtils/useScope";
import {
  GatewayZone,
  listAddresses,
  tunnelZoneMismatches,
} from "../TroubleshootingUtils/siteTunnel";

const FIELD_SX = {
  "& .MuiInputBase-root": { ...MULISH, fontSize: 14, backgroundColor: "#fff" },
  "& input": { py: 1.1 },
};
const LABEL_SX = {
  ...MULISH,
  fontWeight: 700,
  fontSize: 13,
  mb: 0.5,
  display: "block",
  color: TS_COLORS.navy,
};

/**
 * The NAT proxy, shown as "Default path" (Jacob, 2026-09-27). It reaches
 * the public internet and, through the VPC route table, customer networks
 * behind IPsec site-to-site VPNs. It does NOT reach WireGuard subnets: those
 * routes live only on the public EKS nodes WireGuard sites run on (verified
 * 2026-09-27), so a WireGuard-reached address needs its tunnel picked.
 * admin's result label for this origin is the same string.
 */
const NAT_LABEL = "Default path";
/**
 * The port check runs from the NAT or a WireGuard gateway only. Site-to-Site
 * origins are refused by admin until ENG-1521 wires them, and there is no
 * "public node" origin yet for sites whose connector skips the NAT.
 */
const SITE_TO_SITE_ORIGIN_SUPPORTED = false;

/**
 * The picked site's own addresses for the host pickers: its cameras, its
 * servers and the tunnels serving it (ENG-2058). Empty with no site.
 */
const useSiteLocalTargets = (): NetworkTarget[] => {
  const scope = useTabScope();
  const siteDetail = useTroubleshootingStore((st) => st.siteDetail);
  const siteCameras = useTroubleshootingStore((st) => st.siteCameras);
  const recordingServers = useTroubleshootingStore((st) => st.recordingServers);
  const receivers = useTroubleshootingStore((st) => st.receivers);
  const siteTunnels = useScopedTunnels();
  return React.useMemo(
    () =>
      scope === "site"
        ? siteLocalTargets({
            site: siteDetail,
            cameras: siteCameras ?? [],
            tunnels: siteTunnels,
            serverPorts: sitePorts(siteDetail),
            recordingServers: recordingServers ?? [],
            // The site's Immix/Dice receivers (ENG-2063), when loaded.
            alarmReceivers: receivers?.status === "ok" ? receivers.data : [],
          })
        : [],
    [scope, siteDetail, siteCameras, siteTunnels, recordingServers, receivers]
  );
};

const SERVER_LABEL: Partial<Record<NetworkTarget["source"], string>> = {
  server: "Site server",
  management_server: "Management server",
  event_server: "Event server (alarms)",
  recording_server: "Recording server",
  relay: "Vendor cloud",
  alarm_receiver: "Alarm receiver",
};

/** A Test from menu value ("nat" or "wg:<tunnel id>") as a probe origin. */
const originFor = (value: string): ProbeOrigin =>
  value.startsWith("wg:")
    ? { kind: "wireguard", tunnel_id: Number(value.slice(3)) }
    : { kind: "nat" };

// ---------------------------------------------------------------------------
// The picked site's connector: where it leaves from, and tunnels it can't use
// ---------------------------------------------------------------------------

/**
 * With a site picked (ENG-2059): whether its connector leaves through the
 * NAT (`ecs_nat_instance`), and a warning for each tunnel that routes one of
 * its addresses from a gateway in another zone than the site's own
 * (`Customer.wireguard`). The connector runs in its gateway's zone and only
 * that zone's tunnels are routed on its node.
 */
const SiteConnectorPath = () => {
  const scope = useTabScope();
  const siteDetail = useTroubleshootingStore((st) => st.siteDetail);
  const allTunnels = useTroubleshootingStore((st) => st.tunnels);
  const cameraLiveness = useTroubleshootingStore((st) => st.cameraLiveness);
  const siteCameras = useTroubleshootingStore((st) => st.siteCameras);
  const gateways = useAppStore((st) => st.wireGuardOptions) as GatewayZone[];
  const mismatches = React.useMemo(
    () =>
      scope === "site"
        ? tunnelZoneMismatches(
            siteDetail,
            siteCameraHosts(cameraLiveness, siteCameras),
            allTunnels,
            gateways ?? []
          )
        : [],
    [scope, siteDetail, cameraLiveness, siteCameras, allTunnels, gateways]
  );
  if (scope !== "site" || !siteDetail) return null;
  const nat = siteDetail.ecs_nat_instance;
  if (typeof nat !== "boolean" && mismatches.length === 0) return null;
  return (
    <Card testId="site-connector-path" sx={{ p: 2 }}>
      {typeof nat === "boolean" && (
        <Box data-testid="site-connector-nat" sx={{ ...MULISH, fontSize: 14 }}>
          <b>{siteDetail.name}&rsquo;s connector:</b>{" "}
          {nat
            ? "Leaves through Actuate's fixed address (NAT)"
            : "Leaves from a changing address (no NAT)"}
        </Box>
      )}
      {mismatches.map((m) => (
        <Box
          key={m.tunnel.id}
          data-testid={`site-tunnel-zone-warning-${m.tunnel.id}`}
          role="alert"
          sx={{
            ...MULISH,
            fontSize: 14,
            mt: typeof nat === "boolean" ? 1 : 0,
            p: 1.25,
            borderRadius: 1,
            color: TS_COLORS.amber,
            backgroundColor: TS_COLORS.amberTint,
          }}
        >
          {`The connector for ${siteDetail.name} runs in zone ${
            m.siteZone
          }; tunnel ${m.tunnel.name} is in zone ${
            m.tunnelZone
          }, so the connector can't reach ${listAddresses(
            m.addresses
          )} through it.`}
        </Box>
      ))}
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Connectivity analyzer
// ---------------------------------------------------------------------------

const ConnectivityAnalyzer = ({
  onCopy,
}: {
  onCopy: (text: string) => void;
}) => {
  const {
    sampleData,
    siteDetail,
    siteToSite,
    pendingProbe,
    clearPendingProbe,
    probeHistory,
    pushProbeHistory,
    smtpSiteIds,
  } = useTroubleshootingStore();
  const tunnels = useScopedTunnels();
  const allTunnels = useTroubleshootingStore((s) => s.tunnels);
  const localTargets = useSiteLocalTargets();
  const tabScope = useTabScope();
  // With a site picked, the tunnels that route its addresses (by subnet).
  const siteTunnelIds = React.useMemo(
    () =>
      new Set(tabScope === "site" ? tunnels.map((tunnel) => tunnel.id) : []),
    [tabScope, tunnels]
  );
  const getServerName = useServerName();
  const [host, setHost] = React.useState("");
  const [ports, setPorts] = React.useState("554, 80, 8000");
  const routingCount = tunnelsRouting(allTunnels, host).length;
  // Cloud and inbound sites have nothing at the site to reach: say so.
  const siteProfileNote =
    tabScope === "site" && siteDetail ? endpointProfile(siteDetail).note : null;
  // Default path unless the user picks a tunnel (Jacob, 2026-09-27). Never
  // guessed from the address: a WireGuard subnet can overlap an IPsec route.
  const [from, setFrom] = React.useState<string>("nat");
  const [running, setRunning] = React.useState(false);
  const cooldown = useCooldown();
  const [result, setResult] =
    React.useState<GapResult<PortCheckResponse> | null>(null);
  const [probeSite, setProbeSite] = React.useState<SiteConnectionDetail | null>(
    null
  );
  const targetCheck = useTargetCheck(host, originFor(from), localTargets);

  React.useEffect(() => {
    if (pendingProbe) {
      setHost(pendingProbe.host);
      setProbeSite(
        pendingProbe.site ??
          (siteDetail && siteDetail.id === pendingProbe.siteId
            ? siteDetail
            : null)
      );
      if (pendingProbe.ports?.length) setPorts(pendingProbe.ports.join(", "));
      setResult(null);
      clearPendingProbe();
    }
  }, [pendingProbe, clearPendingProbe]);

  const s2s = siteToSite?.status === "ok" ? siteToSite.data : [];
  // Connections has no scope bar, so the site comes from the "Check
  // reachability" hand-off: only then do we know whose egress to compare.
  const skipsNat =
    probeSite != null &&
    probeSite.ecs_nat_instance === false &&
    connectionKindForSite(probeSite, smtpSiteIds) !== "wireguard";

  const resolveOrigin = (): { origin: ProbeOrigin; label: string } => {
    if (from === "nat") return { origin: { kind: "nat" }, label: NAT_LABEL };
    if (from.startsWith("wg:")) {
      const id = Number(from.slice(3));
      const tunnel = allTunnels.find((t) => t.id === id);
      return {
        origin: { kind: "wireguard", tunnel_id: id },
        label: `WireGuard gateway ${getServerName(
          tunnel?.server ?? null
        )} via ${tunnel?.name || id}`,
      };
    }
    const id = from.slice(4);
    const conn = s2s.find((c) => c.id === id);
    return {
      origin: { kind: "site-to-site", connection_id: id },
      label: `Site-to-Site tunnel ${conn?.name.split(",")[0] || id}`,
    };
  };

  const run = async () => {
    const portList = parsePortList(ports);
    if (!host.trim() || portList.length === 0 || !targetCheck.allowed) return;
    const { origin, label } = resolveOrigin();
    setRunning(true);
    const response = await runPortCheck(
      { host: host.trim(), ports: portList, from: origin },
      label,
      sampleData
    );
    setRunning(false);
    setResult(response);
    cooldown.start(
      response.status === "error" && response.retryAfterSeconds
        ? response.retryAfterSeconds
        : PORT_CHECK_COOLDOWN_SECONDS
    );
    if (response.status === "ok") pushProbeHistory(response.data);
  };

  const verdict = (data: PortCheckResponse) => {
    const filtered = data.results
      .filter((r) => r.result === "filtered")
      .map((r) => r.port);
    const closed = data.results
      .filter((r) => r.result === "closed")
      .map((r) => r.port);
    const open = data.results
      .filter((r) => r.result === "open")
      .map((r) => r.port);
    const errored = data.results.filter((r) => r.result === "error");
    if (errored.length === data.results.length && errored.length) {
      const dns = errored.some((r) => r.detail === "dns_failed");
      return (
        <>
          <Pill tone="grey">Could not probe</Pill>{" "}
          {dns
            ? `${data.from_label} could not resolve ${data.host}. Check the hostname, or use the device's IP address.`
            : `The probe hit an error on every port (${
                errored[0].detail || "unknown"
              }). Try again, or use the device's IP address.`}
        </>
      );
    }
    if (filtered.length && !open.length)
      return (
        <>
          <Pill tone="orange">Likely on your side</Pill> Nothing on{" "}
          <Mono>{data.host}</Mono> answered within the probe window, and the
          host did not refuse either. Most often that is a firewall or NAT rule
          between {data.from_label} and this host for ports{" "}
          {filtered.join(", ")}. It can also be a rate limit on the return path
          toward our address — replies that arrive 10–30 s late look exactly
          like a drop, so if another checker says the port is open, compare the
          rule that names our address with one that works. If the host is also
          unreachable from inside your own network, it is powered off or on a
          different subnet.
        </>
      );
    if (filtered.length)
      return (
        <>
          <Pill tone="orange">Likely on your side</Pill> The device is up (ports{" "}
          {open.join(", ")} answered) but ports {filtered.join(", ")} did not
          answer within the probe window. Allow them through the firewall or NAT
          toward the device — or, if they are allowed, check for a rate limit on
          traffic to our address — then run the test again.
        </>
      );
    if (closed.length && !open.length)
      return (
        <>
          <Pill tone="orange">Fix is on your side</Pill> The host is reachable
          but refuses ports {closed.join(", ")}. Confirm the service is running
          and that these are the right ports for this device model.
        </>
      );
    if (closed.length)
      return (
        <>
          <Pill tone="ok">Reachable</Pill> Ports {open.join(", ")} are open.
          Ports {closed.join(", ")} are closed, which is fine if you don&rsquo;t
          use them.
        </>
      );
    return (
      <>
        <Pill tone="ok">Reachable</Pill> Every port answered. If the camera is
        still offline, the problem is credentials or the stream itself: run the
        Credential test below or the WireGuard Debugger.
      </>
    );
  };

  const resultText = (data: PortCheckResponse) =>
    [
      `Connectivity check ${data.host} from ${data.from_label} at ${data.checked_at}`,
      ...data.results.map(
        (r) =>
          `  port ${r.port}: ${PORT_LABEL[r.result].toLowerCase()}${
            r.ms != null ? ` (${r.ms} ms)` : ""
          }`
      ),
    ].join("\n");

  return (
    <Card>
      <CardHeader
        title="Connectivity analyzer"
        hint="Test whether a host and port are reachable from Actuate. Most offline cameras come down to a filtered port, and this check names it in about 5 seconds."
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            lg: "minmax(0,1.6fr) minmax(0,1fr) minmax(0,1.3fr) auto",
          },
          gap: 1.5,
          alignItems: "start",
          p: 2.5,
        }}
      >
        <Box>
          <Typography component="label" htmlFor="ts-ca-host" sx={LABEL_SX}>
            Host
          </Typography>
          <TargetHostField
            id="ts-ca-host"
            value={host}
            onChange={setHost}
            onPick={(target) => {
              if (target.ports.length) setPorts(target.ports.join(", "));
            }}
            origin={originFor(from)}
            localTargets={localTargets}
            placeholder="Search your addresses, or type a public IP"
          />
          {siteProfileNote && (
            <Box data-testid="analyzer-site-note" sx={{ mt: 0.5 }}>
              <Hint>{siteProfileNote}</Hint>
            </Box>
          )}
        </Box>
        <Box>
          <Typography component="label" htmlFor="ts-ca-ports" sx={LABEL_SX}>
            Ports
          </Typography>
          <TextField
            id="ts-ca-ports"
            size="small"
            fullWidth
            value={ports}
            onChange={(e) => setPorts(e.target.value)}
            placeholder="554, 80, 8000"
            sx={FIELD_SX}
          />
          <Hint>
            Comma separated. RTSP 554, ONVIF/HTTP 80, Hik 8000, Dahua 37777,
            Milestone 7563.
          </Hint>
        </Box>
        <Box>
          <Typography component="label" htmlFor="ts-ca-from" sx={LABEL_SX}>
            Test from
          </Typography>
          <Select
            id="ts-ca-from"
            size="small"
            fullWidth
            value={from}
            onChange={(e) => setFrom(String(e.target.value))}
            sx={{ ...MULISH, fontSize: 14, backgroundColor: "#fff" }}
          >
            <MenuItem value="nat" sx={MULISH}>
              {NAT_LABEL}
            </MenuItem>
            {tunnelMenuItems(allTunnels, host, siteTunnelIds)}
            {SITE_TO_SITE_ORIGIN_SUPPORTED &&
              s2s.map((c) => (
                <MenuItem key={c.id} value={`s2s:${c.id}`} sx={MULISH}>
                  Site-to-Site: {c.name.split(",")[0]}
                </MenuItem>
              ))}
            <MenuItem value="public" disabled sx={MULISH}>
              A public address other than the default path (coming soon)
            </MenuItem>
          </Select>
          <Hint>
            The default path reaches the internet and networks behind IPsec
            site-to-site VPNs. For a device behind WireGuard, pick its tunnel:
            the default path does not reach WireGuard networks.
          </Hint>
          {routingCount > 1 && (
            <Box data-testid="analyzer-tunnel-overlap" sx={{ mt: 0.5 }}>
              <Hint>
                More than one tunnel lists this address, so their networks
                overlap. Pick the one this device is really behind.
              </Hint>
            </Box>
          )}
          {skipsNat && (
            <Box data-testid="analyzer-skips-nat" sx={{ mt: 0.5 }}>
              <Hint>
                This site&rsquo;s connector does not use the default path: it
                reaches the site from a changing public address. A default-path
                test can fail here while the connector connects fine, or the
                other way round.
              </Hint>
            </Box>
          )}
        </Box>
        <Box sx={{ pt: { lg: "22px" } }}>
          <TsButton
            onClick={run}
            disabled={
              running ||
              cooldown.remaining > 0 ||
              !host.trim() ||
              !targetCheck.allowed ||
              parsePortList(ports).length === 0
            }
            sx={{ height: 40, minWidth: 110 }}
          >
            {running
              ? "Testing…"
              : cooldown.remaining > 0
              ? waitLabel(cooldown.remaining)
              : "Run test"}
          </TsButton>
        </Box>
      </Box>
      <Box
        sx={{ display: "flex", gap: 2.25, flexWrap: "wrap", px: 2.5, pb: 1.75 }}
      >
        <Hint>
          <Pill tone="ok">Open</Pill> something answered on that port
        </Hint>
        <Hint>
          <Pill tone="warn">Closed</Pill> the host replied but nothing is
          listening there; wrong port or the service is stopped
        </Hint>
        <Hint>
          <Pill tone="bad">No reply</Pill> nothing came back within the probe
          window. Usually a firewall or NAT rule dropping the traffic — but a
          throttled return path looks identical, so on its own this is not proof
          of a rule
        </Hint>
      </Box>
      {result && result.status !== "ok" && (
        <GapNotice result={result} what="The connectivity check" kind="check" />
      )}
      {result && result.status === "ok" && (
        <Box
          sx={{
            px: 2.5,
            pb: 2.25,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1.25,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Hint>
              {result.data.host} from {result.data.from_label}
            </Hint>
            {result.data.ping && (
              <Pill tone={result.data.ping.reply ? "ok" : "bad"}>
                Ping{" "}
                {result.data.ping.reply
                  ? `reply ${result.data.ping.ms ?? "?"} ms`
                  : "no reply"}
              </Pill>
            )}
            {result.sample && <SampleChip />}
          </Box>
          <Box
            sx={{
              border: `1px solid ${TS_COLORS.line}`,
              borderRadius: "4px",
              overflow: "auto",
            }}
          >
            <ResponsiveTable>
              <TableHead>
                <TableRow>
                  {["Port", "Result", "Time", "What it means"].map((h) => (
                    <TableCell key={h} sx={HEAD_CELL_SX}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {result.data.results.map((r) => (
                  <TableRow
                    key={r.port}
                    sx={
                      r.result === "filtered"
                        ? { backgroundColor: "#fffaf5" }
                        : undefined
                    }
                  >
                    <TableCell sx={{ ...CELL_SX, fontWeight: 800 }}>
                      {r.port}
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      <Pill tone={PORT_TONE[r.result]}>
                        {PORT_LABEL[r.result]}
                      </Pill>
                    </TableCell>
                    <TableCell
                      sx={{ ...CELL_SX, fontVariantNumeric: "tabular-nums" }}
                    >
                      {r.result === "error"
                        ? "—"
                        : r.ms == null
                        ? "timeout, 3.0 s"
                        : `${r.ms} ms`}
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      <Hint>
                        {r.result === "open"
                          ? "A service answered. This port is not the problem."
                          : r.result === "closed"
                          ? "The host replied “nothing here”. Wrong port or the service is stopped."
                          : r.result === "error"
                          ? r.detail === "dns_failed"
                            ? "The hostname did not resolve from the origin."
                            : `The probe could not run (${
                                r.detail || "error"
                              }).`
                          : r.detail === "unreachable"
                          ? "No route to the host from the origin. It is on a network the origin cannot reach."
                          : "Timed out with no reply. A firewall or NAT rule is dropping it."}
                      </Hint>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </ResponsiveTable>
          </Box>
          <Typography
            sx={{ ...MULISH, fontSize: 15, lineHeight: 1.5, maxWidth: "90ch" }}
          >
            {verdict(result.data)}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TsButton
              variant="quiet"
              size="sm"
              onClick={() => onCopy(resultText(result.data))}
            >
              Copy result for support
            </TsButton>
            <TsButton
              variant="quiet"
              size="sm"
              onClick={run}
              disabled={running}
            >
              Run again
            </TsButton>
          </Box>
        </Box>
      )}
      {probeHistory.length > 0 && (
        <Box
          sx={{ borderTop: `1px solid ${TS_COLORS.line}`, overflowX: "auto" }}
        >
          <ResponsiveTable>
            <TableHead>
              <TableRow>
                {["Ran", "Host", "From", "Result", ""].map((h) => (
                  <TableCell key={h} sx={HEAD_CELL_SX}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {probeHistory.slice(0, 4).map((h) => (
                <TableRow key={`${h.host}-${h.checked_at}`}>
                  <TableCell sx={CELL_SX}>
                    {formatRelative(h.checked_at)}
                  </TableCell>
                  <TableCell sx={CELL_SX}>
                    <Mono>{h.host}</Mono>
                  </TableCell>
                  <TableCell sx={CELL_SX}>
                    <Hint>{h.from_label}</Hint>
                  </TableCell>
                  <TableCell sx={CELL_SX}>
                    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                      {h.results.map((r) => (
                        <Pill key={r.port} tone={PORT_TONE[r.result]}>
                          {r.port} {r.result}
                        </Pill>
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell sx={CELL_SX}>
                    <TsButton
                      variant="ghost"
                      size="sm"
                      onClick={() => onCopy(resultText(h))}
                    >
                      Copy
                    </TsButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ResponsiveTable>
        </Box>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Credential test
// ---------------------------------------------------------------------------

const CredentialTest = ({ onCopy }: { onCopy: (text: string) => void }) => {
  const {
    sampleData,
    siteDetail,
    pendingCredentialPrefill,
    clearPendingCredentialPrefill,
  } = useTroubleshootingStore();
  const canViewPasswords = Boolean(
    useAppStore((s) => s.userPermissions)?.view_passwords
  );
  const [filledFrom, setFilledFrom] = React.useState<CredentialPrefill | null>(
    null
  );
  const cooldown = useCooldown();
  const siteTunnels = useScopedTunnels();
  const localTargets = useSiteLocalTargets();
  const allTunnels = useTroubleshootingStore((s) => s.tunnels);
  const tabScope = useTabScope();
  const siteTunnelIds = React.useMemo(
    () =>
      new Set(
        tabScope === "site" ? siteTunnels.map((tunnel) => tunnel.id) : []
      ),
    [tabScope, siteTunnels]
  );
  const [credFrom, setCredFrom] = React.useState<string>("nat");
  const [host, setHost] = React.useState("");
  const [port, setPort] = React.useState("80");
  const [rtspPort, setRtspPort] = React.useState("554");
  const [user, setUser] = React.useState("admin");
  const [pw, setPw] = React.useState("");
  const targetCheck = useTargetCheck(host, originFor(credFrom), localTargets);
  const [running, setRunning] = React.useState(false);
  const [result, setResult] =
    React.useState<GapResult<CredentialCheckResponse> | null>(null);

  const mounted = React.useRef(true);
  React.useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  // "Check reachability" fills the form with the site's saved settings so the
  // test tries exactly what the connector uses. The login only for users who
  // may see it (view_passwords); admin masks it for everyone else anyway.
  React.useEffect(() => {
    if (!pendingCredentialPrefill) return;
    const prefill = pendingCredentialPrefill;
    clearPendingCredentialPrefill();
    const apply = (camera: CameraLogin | null) => {
      if (!mounted.current) return;
      const next = buildCredentialPrefill({
        prefill,
        detail: prefill.site ?? siteDetail,
        canViewPasswords,
        camera,
      });
      setHost(next.host);
      if (next.port) setPort(next.port);
      if (next.rtspPort) setRtspPort(next.rtspPort);
      setUser(next.username ?? (next.withheld ? "" : "admin"));
      setPw(next.password ?? "");
      setResult(null);
      setFilledFrom(next);
    };
    const known = prefill.site ?? siteDetail;
    const siteHasLogin =
      known?.id === prefill.siteId &&
      Boolean(known?.username || known?.password);
    const cameraId = prefill.cameraIds?.[0];
    if (canViewPasswords && !siteHasLogin && cameraId != null && !sampleData) {
      fetchCameraLogin(cameraId)
        .then(apply)
        .catch(() => apply(null));
    } else {
      apply(null);
    }
  }, [
    pendingCredentialPrefill,
    clearPendingCredentialPrefill,
    siteDetail,
    canViewPasswords,
    sampleData,
  ]);

  const run = async () => {
    if (!host.trim() || !targetCheck.allowed) return;
    setRunning(true);
    // Default path unless the user picked a tunnel: never inferred from the
    // address (an IPsec route can overlap a WireGuard subnet).
    const origin = originFor(credFrom);
    const response = await runCredentialCheck(
      {
        host: host.trim(),
        port: Number(port) || 80,
        rtsp_port: Number(rtspPort) || 554,
        username: user,
        password: pw,
        from: origin,
      },
      sampleData
    );
    setRunning(false);
    setResult(response);
    // Every run is a login attempt on the customer's device; the server caps
    // attempts per host, and this keeps a double-click from spending two.
    cooldown.start(
      response.status === "error" && response.retryAfterSeconds
        ? response.retryAfterSeconds
        : CREDENTIAL_CHECK_COOLDOWN_SECONDS
    );
  };

  const Row = ({
    check,
    sub,
    ok,
    said,
  }: {
    check: string;
    sub: string;
    ok: boolean | null;
    said: string;
  }) => (
    <TableRow sx={ok === false ? { backgroundColor: "#fffaf5" } : undefined}>
      <TableCell sx={CELL_SX}>
        <b>{check}</b> <Hint>{sub}</Hint>
      </TableCell>
      <TableCell sx={CELL_SX}>
        {ok == null ? (
          <Pill tone="grey">Skipped</Pill>
        ) : (
          <Pill tone={ok ? "ok" : "bad"}>
            {ok ? "Authenticated" : "Rejected"}
          </Pill>
        )}
      </TableCell>
      <TableCell sx={CELL_SX}>
        <Hint>{said}</Hint>
      </TableCell>
    </TableRow>
  );

  return (
    <Card>
      <CardHeader
        title="Credential test"
        hint="Try the camera or NVR login from Actuate's side and see the device's actual answer, so a wrong password stops looking like “camera offline”."
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            lg: "minmax(0,1.4fr) minmax(0,0.5fr) minmax(0,0.5fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1.2fr) auto",
          },
          gap: 1.5,
          alignItems: "start",
          p: 2.5,
        }}
      >
        <Box>
          <Typography component="label" htmlFor="ts-cr-host" sx={LABEL_SX}>
            Host
          </Typography>
          <TargetHostField
            id="ts-cr-host"
            value={host}
            onChange={setHost}
            origin={originFor(credFrom)}
            localTargets={localTargets}
            placeholder="Search your addresses, or type a public IP"
          />
        </Box>
        <Box>
          <Typography component="label" htmlFor="ts-cr-port" sx={LABEL_SX}>
            Port
          </Typography>
          <TextField
            id="ts-cr-port"
            size="small"
            fullWidth
            value={port}
            onChange={(e) => setPort(e.target.value)}
            sx={FIELD_SX}
          />
          <Hint>ONVIF/HTTP, usually 80</Hint>
        </Box>
        <Box>
          <Typography component="label" htmlFor="ts-cr-rtsp" sx={LABEL_SX}>
            RTSP port
          </Typography>
          <TextField
            id="ts-cr-rtsp"
            size="small"
            fullWidth
            value={rtspPort}
            onChange={(e) => setRtspPort(e.target.value)}
            sx={FIELD_SX}
          />
          <Hint>Usually 554</Hint>
        </Box>
        <Box>
          <Typography component="label" htmlFor="ts-cr-user" sx={LABEL_SX}>
            Username
          </Typography>
          <TextField
            id="ts-cr-user"
            size="small"
            fullWidth
            value={user}
            onChange={(e) => setUser(e.target.value)}
            sx={FIELD_SX}
          />
        </Box>
        <Box>
          <Typography component="label" htmlFor="ts-cr-pw" sx={LABEL_SX}>
            Password
          </Typography>
          <TextField
            id="ts-cr-pw"
            type="password"
            size="small"
            fullWidth
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            sx={FIELD_SX}
            placeholder="Not saved to the camera"
            autoComplete="off"
          />
        </Box>
        <Box>
          <Typography component="label" htmlFor="ts-cr-from" sx={LABEL_SX}>
            Test from
          </Typography>
          <Select
            id="ts-cr-from"
            size="small"
            fullWidth
            value={credFrom}
            onChange={(e) => setCredFrom(String(e.target.value))}
            sx={{ ...MULISH, fontSize: 14, backgroundColor: "#fff" }}
          >
            <MenuItem value="nat" sx={MULISH}>
              {NAT_LABEL}
            </MenuItem>
            {tunnelMenuItems(allTunnels, host, siteTunnelIds)}
          </Select>
        </Box>
        <Box sx={{ pt: { lg: "22px" } }}>
          <TsButton
            onClick={run}
            disabled={
              running ||
              cooldown.remaining > 0 ||
              !host.trim() ||
              !targetCheck.allowed
            }
            sx={{ height: 40, minWidth: 110 }}
          >
            {running
              ? "Testing…"
              : cooldown.remaining > 0
              ? waitLabel(cooldown.remaining)
              : "Test login"}
          </TsButton>
        </Box>
      </Box>
      {filledFrom && (
        <Box sx={{ px: 2.5, pb: 1.5 }} data-testid="credential-prefill-note">
          <Hint>
            {filledFrom.loginSource === "site"
              ? "Filled from this site's saved settings, including its saved login. Nothing you change here is saved."
              : filledFrom.loginSource === "camera"
              ? "Filled from this site's saved settings and the first affected camera's saved login. Nothing you change here is saved."
              : filledFrom.withheld
              ? "Host and ports are filled from this site's saved settings. The saved login is not filled in: seeing it needs the View passwords permission."
              : "Host and ports are filled from this site's saved settings. It has no saved login to fill in."}
          </Hint>
        </Box>
      )}
      {result && result.status !== "ok" && (
        <GapNotice result={result} what="The login test" kind="check" />
      )}
      {result && result.status === "ok" && (
        <Box
          sx={{
            px: 2.5,
            pb: 2.25,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          {result.sample && (
            <Box>
              <SampleChip />
            </Box>
          )}
          <Box
            sx={{
              border: `1px solid ${TS_COLORS.line}`,
              borderRadius: "4px",
              overflow: "auto",
            }}
          >
            <ResponsiveTable>
              <TableHead>
                <TableRow>
                  {["Check", "Result", "Device said"].map((h) => (
                    <TableCell key={h} sx={HEAD_CELL_SX}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={CELL_SX}>
                    <b>TCP connect</b>{" "}
                    <Hint>
                      {host}:{port}
                    </Hint>
                  </TableCell>
                  <TableCell sx={CELL_SX}>
                    <Pill tone={result.data.tcp.ok ? "ok" : "bad"}>
                      {result.data.tcp.ok ? "Open" : "No reply"}
                    </Pill>
                  </TableCell>
                  <TableCell sx={CELL_SX}>
                    <Hint>
                      {result.data.tcp.ok
                        ? `Connected in ${result.data.tcp.ms ?? "?"} ms`
                        : "No reply"}
                    </Hint>
                  </TableCell>
                </TableRow>
                <Row
                  check="ONVIF GetDeviceInformation"
                  sub="WS-Security digest"
                  ok={result.data.onvif.ok}
                  said={
                    result.data.onvif.detail +
                    (result.data.device
                      ? `. ${[
                          result.data.device.manufacturer,
                          result.data.device.model,
                          result.data.device.firmware,
                        ]
                          .filter(Boolean)
                          .join(" ")}`
                      : "")
                  }
                />
                <Row
                  check="RTSP DESCRIBE"
                  sub={`rtsp://${host}:${rtspPort || 554}/`}
                  ok={result.data.rtsp.ok}
                  said={result.data.rtsp.detail}
                />
              </TableBody>
            </ResponsiveTable>
          </Box>
          <Typography
            sx={{ ...MULISH, fontSize: 15, lineHeight: 1.5, maxWidth: "90ch" }}
          >
            {!result.data.tcp.ok ? (
              <>
                <Pill tone="grey">Not reachable</Pill> Nothing answered on{" "}
                {host}:{port}
                {result.data.from_label
                  ? ` from ${result.data.from_label}`
                  : ""}
                , so the credentials were never tested. Use the Connectivity
                analyzer above to find the port or firewall rule in the way.
              </>
            ) : result.data.onvif.ok && result.data.rtsp.ok ? (
              <>
                <Pill tone="ok">Credentials work</Pill> The device accepts this
                username and password on both ONVIF and RTSP. If the camera
                still shows offline in Actuate, the saved credentials differ
                from what you typed here: update them on the camera in Actuate.
              </>
            ) : (
              <>
                <Pill tone="orange">Fix is on your side</Pill> The device is
                reachable and answering, but it rejects this username and
                password. Check for a recent password change or a factory reset
                on the camera, and confirm the account is not locked out.
                Nothing here is saved; update the camera&rsquo;s credentials in
                Actuate once you have the right ones.
              </>
            )}
          </Typography>
          {(result.data.from_label ||
            result.data.attempts_remaining != null) && (
            <Hint>
              {result.data.from_label
                ? `Tested from ${result.data.from_label}. `
                : ""}
              {result.data.attempts_remaining != null
                ? `${result.data.attempts_remaining} attempt${
                    result.data.attempts_remaining === 1 ? "" : "s"
                  } left on this host in the next 10 minutes, so a wrong password cannot lock the device account.`
                : ""}
            </Hint>
          )}
          <Box>
            <TsButton
              variant="quiet"
              size="sm"
              onClick={() =>
                onCopy(
                  `Credential test ${host}:${port} as ${user}\nTCP: ${
                    result.data.tcp.ok ? "open" : "no reply"
                  }\nONVIF: ${result.data.onvif.detail}\nRTSP: ${
                    result.data.rtsp.detail
                  }`
                )
              }
            >
              Copy result for support
            </TsButton>
          </Box>
        </Box>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Site-to-Site VPN
// ---------------------------------------------------------------------------

/** Last Site-to-Site check per connection, so re-expanding a row reuses it. */
const recentS2sChecks: Record<
  string,
  { at: number; result: GapResult<SiteToSiteCheckResult> }
> = {};

const S2sCheckRow = ({
  connection,
  onCopy,
}: {
  connection: SiteToSiteConnection;
  onCopy: (text: string) => void;
}) => {
  const { sampleData } = useTroubleshootingStore();
  const [result, setResult] =
    React.useState<GapResult<SiteToSiteCheckResult> | null>(null);
  const [running, setRunning] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    // Collapsing and re-expanding a row re-mounts it; within the cooldown the
    // last answer is shown instead of queueing another gateway round trip.
    const recent = recentS2sChecks[connection.id];
    if (
      recent &&
      !sampleData &&
      Date.now() - recent.at < S2S_CHECK_COOLDOWN_SECONDS * 1000
    ) {
      setResult(recent.result);
      setRunning(false);
      return undefined;
    }
    setRunning(true);
    runSiteToSiteCheck(connection, sampleData).then((r) => {
      recentS2sChecks[connection.id] = { at: Date.now(), result: r };
      if (cancelled) return;
      setResult(r);
      setRunning(false);
    });
    return () => {
      cancelled = true;
    };
  }, [connection, sampleData]);
  // Drawn before the first response so the shape is stable; titles come from
  // the API once it answers.
  const order: { id: SiteToSiteRungId; title: string }[] = [
    { id: "tunnel_status", title: "Tunnel status" },
    { id: "route_present", title: "Route to target" },
    { id: "tunnel_reachability", title: "Target reachability (TCP)" },
    { id: "vms_connectivity", title: "VMS connectivity" },
  ];
  const check = result?.status === "ok" ? result.data : null;
  const rungs = check?.rungs ?? [];
  const verdict = check?.classification ?? null;
  const verdictPill = verdict
    ? verdict.code === "healthy"
      ? { tone: "ok" as const, text: "Healthy" }
      : verdict.owner === "customer"
      ? {
          tone: "orange" as const,
          text: `Fix is on your side: ${verdict.label}`,
        }
      : verdict.owner === "actuate"
      ? { tone: "bad" as const, text: `Fix is on our side: ${verdict.label}` }
      : { tone: "grey" as const, text: verdict.label }
    : null;
  return (
    <TableRow>
      <TableCell colSpan={7} sx={{ backgroundColor: "#fafafb", px: 2, pb: 2 }}>
        {result && result.status !== "ok" ? (
          <GapNotice
            result={result}
            what="The Site-to-Site check"
            kind="check"
          />
        ) : (
          <>
            <Box sx={{ pt: 1 }}>
              {order.map((step, index) => {
                const rung = rungs.find((r) => r.id === step.id);
                return (
                  <LadderRung
                    key={step.id}
                    index={index + 1}
                    title={rung?.title ?? step.title}
                    status={
                      running
                        ? index === 0
                          ? "running"
                          : "pending"
                        : rung?.status ?? "pending"
                    }
                    detail={
                      running
                        ? "Checking…"
                        : [rung?.summary ?? "Not started.", rung?.next_action]
                            .filter(Boolean)
                            .join(" ")
                    }
                    isLast={index === order.length - 1}
                  />
                );
              })}
            </Box>
            {!running && (
              <Box
                sx={{
                  display: "flex",
                  gap: 1.25,
                  alignItems: "center",
                  mt: 1,
                  flexWrap: "wrap",
                }}
              >
                <Hint>
                  {verdict?.summary ||
                    "Read from AWS gateway telemetry and our probe pod, no agent needed on your side."}
                </Hint>
                {verdictPill && (
                  <Pill tone={verdictPill.tone}>{verdictPill.text}</Pill>
                )}
                {result?.status === "ok" && result.sample && <SampleChip />}
                <Box sx={{ flex: 1 }} />
                <TsButton
                  variant="quiet"
                  size="sm"
                  onClick={() =>
                    onCopy(
                      [
                        `Site-to-Site check ${connection.name}`,
                        ...rungs.map(
                          (r) => `  ${r.title}: ${r.status} — ${r.summary}`
                        ),
                        verdict
                          ? `  Verdict: ${verdict.label} (${verdict.owner}, ${verdict.confidence} confidence) — ${verdict.summary}`
                          : "",
                      ]
                        .filter(Boolean)
                        .join("\n")
                    )
                  }
                >
                  Copy report
                </TsButton>
              </Box>
            )}
          </>
        )}
      </TableCell>
    </TableRow>
  );
};

const SiteToSiteCard = ({ onCopy }: { onCopy: (text: string) => void }) => {
  const { siteToSite, openS2sChecks, setS2sCheckOpen } =
    useTroubleshootingStore();
  const scope = useTabScope();
  const scopedSites = useScopedSites();
  const scopedIds = new Set(scopedSites.map((s) => s.id));
  if (!siteToSite) return null;
  if (siteToSite.status !== "ok") {
    return (
      <Card>
        <CardHeader
          leading={<KindChip kind="site-to-site" />}
          title="Site-to-Site VPN"
          hint="IPsec connections we terminate on AWS. Two tunnels each; one down is degraded, both down is down."
        />
        <GapNotice result={siteToSite} what="Site-to-Site VPN status" />
      </Card>
    );
  }
  const rows = siteToSite.data.filter(
    (c) =>
      c.kind === "aws_site_to_site" &&
      (scope === "all" || c.site_ids.some((id) => scopedIds.has(id)))
  );
  if (rows.length === 0) return null;
  return (
    <Card>
      <CardHeader
        leading={<KindChip kind="site-to-site" />}
        title="Site-to-Site VPN"
        hint="IPsec connections we terminate on AWS, read from AWS's own tunnel telemetry. One tunnel down is degraded, both down is down. Press Check on a row to run the four-step connection check."
        right={siteToSite.sample ? <SampleChip /> : undefined}
      />
      <Box sx={{ overflowX: "auto" }}>
        <ResponsiveTable minWidth={1000}>
          <TableHead>
            <TableRow>
              {[
                "Connection",
                "Your peer IP",
                "Tunnels",
                "Probe target",
                "Sites",
                "",
              ].map((h) => (
                <TableCell key={h} sx={SHADED_HEAD_CELL_SX}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((c) => {
              const glance = c.status;
              const down = glance.status === "down";
              const open = Boolean(openS2sChecks[c.id]);
              const tone =
                glance.status === "up"
                  ? "ok"
                  : glance.status === "down"
                  ? "bad"
                  : glance.status === "degraded"
                  ? "warn"
                  : "grey";
              const tunnelsLabel =
                glance.tunnels_total != null && glance.tunnels_up != null
                  ? `${glance.tunnels_up} of ${glance.tunnels_total} up`
                  : glance.status === "disabled"
                  ? "Disabled"
                  : "Unknown";
              return (
                <React.Fragment key={c.id}>
                  <TableRow
                    hover
                    sx={{ backgroundColor: down ? "#fffaf5" : undefined }}
                    data-testid={`s2s-row-${c.id}`}
                  >
                    <TableCell sx={CELL_SX}>
                      <b>{c.name}</b>
                      <Box>
                        <Hint>{c.actuate_gateway}</Hint>
                      </Box>
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      {c.customer_gateway_ip ? (
                        <Mono>{c.customer_gateway_ip}</Mono>
                      ) : (
                        <Hint>—</Hint>
                      )}
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      <StatusDot tone={tone} label={tunnelsLabel} />
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      {c.probe_target ? (
                        <>
                          <Mono>
                            {c.probe_target}
                            {c.probe_port ? `:${c.probe_port}` : ""}
                          </Mono>
                          {c.probe_label && (
                            <Box>
                              <Hint>{c.probe_label}</Hint>
                            </Box>
                          )}
                        </>
                      ) : (
                        <Hint>none configured</Hint>
                      )}
                    </TableCell>
                    <TableCell
                      sx={{ ...CELL_SX, fontVariantNumeric: "tabular-nums" }}
                    >
                      {c.site_ids.length}
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      <TsButton
                        variant="ghost"
                        size="sm"
                        aria-expanded={open}
                        onClick={() => setS2sCheckOpen(c.id, !open)}
                      >
                        {open ? "Hide check" : "Check"}
                      </TsButton>
                    </TableCell>
                  </TableRow>
                  {open && <S2sCheckRow connection={c} onCopy={onCopy} />}
                </React.Fragment>
              );
            })}
          </TableBody>
        </ResponsiveTable>
      </Box>
    </Card>
  );
};

/**
 * Direct paths: a public address the customer forwards to us, with the probe
 * target admin checks (`network_path/` rows of kind `direct`). Same glance
 * and the same ladder as Site-to-Site — the two tunnel steps come back
 * skipped, the reachability and VMS steps are the ones that matter.
 */
const DirectPathsCard = ({ onCopy }: { onCopy: (text: string) => void }) => {
  const { siteToSite, openS2sChecks, setS2sCheckOpen } =
    useTroubleshootingStore();
  const scope = useTabScope();
  const scopedSites = useScopedSites();
  const scopedIds = new Set(scopedSites.map((s) => s.id));
  if (!siteToSite || siteToSite.status !== "ok") return null;
  const rows = siteToSite.data.filter(
    (c) =>
      c.kind === "direct" &&
      (scope === "all" || c.site_ids.some((id) => scopedIds.has(id)))
  );
  if (rows.length === 0) return null;
  return (
    <Card testId="direct-paths-card">
      <CardHeader
        leading={<KindChip kind="direct" />}
        title="Direct connections"
        hint="No VPN: we reach the server on a public address you forward to us. The status is whether that address answered our last probe. Press Check on a row to run the connection check."
        right={siteToSite.sample ? <SampleChip /> : undefined}
      />
      <Box sx={{ overflowX: "auto" }}>
        <ResponsiveTable minWidth={800}>
          <TableHead>
            <TableRow>
              {["Connection", "Probe target", "Reachable", "Sites", ""].map(
                (h) => (
                  <TableCell key={h} sx={HEAD_CELL_SX}>
                    {h}
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((c) => {
              const glance = c.status.status;
              const down = glance === "down";
              const open = Boolean(openS2sChecks[c.id]);
              const tone =
                glance === "up"
                  ? "ok"
                  : glance === "down"
                  ? "bad"
                  : glance === "degraded"
                  ? "warn"
                  : "grey";
              const label =
                glance === "up"
                  ? "Answering"
                  : glance === "down"
                  ? "Not answering"
                  : glance === "degraded"
                  ? "Intermittent"
                  : glance === "disabled"
                  ? "Disabled"
                  : "Not checked yet";
              return (
                <React.Fragment key={c.id}>
                  <TableRow
                    hover
                    sx={{ backgroundColor: down ? "#fffaf5" : undefined }}
                    data-testid={`direct-row-${c.id}`}
                  >
                    <TableCell sx={CELL_SX}>
                      <b>{c.name}</b>
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      {c.probe_target ? (
                        <>
                          <Mono>
                            {c.probe_target}
                            {c.probe_port ? `:${c.probe_port}` : ""}
                          </Mono>
                          {c.probe_label && (
                            <Box>
                              <Hint>{c.probe_label}</Hint>
                            </Box>
                          )}
                        </>
                      ) : (
                        <Hint>none configured</Hint>
                      )}
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      <StatusDot tone={tone} label={label} />
                    </TableCell>
                    <TableCell
                      sx={{ ...CELL_SX, fontVariantNumeric: "tabular-nums" }}
                    >
                      {c.site_ids.length}
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      <TsButton
                        variant="ghost"
                        size="sm"
                        aria-expanded={open}
                        onClick={() => setS2sCheckOpen(c.id, !open)}
                      >
                        {open ? "Hide check" : "Check"}
                      </TsButton>
                    </TableCell>
                  </TableRow>
                  {open && <S2sCheckRow connection={c} onCopy={onCopy} />}
                </React.Fragment>
              );
            })}
          </TableBody>
        </ResponsiveTable>
      </Box>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Tab
// ---------------------------------------------------------------------------

export const ConnectionsTab = ({
  onCopy,
}: {
  onCopy: (text: string) => void;
}) => {
  const {
    openTabForSite,
    setWgSubTab,
    siteId,
    siteDetail,
    smtpSiteIds,
    siteDetailLoading,
    requestProbe,
  } = useTroubleshootingStore();
  const scope = useTabScope();
  const tunnels = useScopedTunnels();
  const scopedSites = useScopedSites();
  const smtpCount = scopedSites.filter((s) =>
    smtpSiteIds.includes(s.id)
  ).length;
  const siteKind = connectionKindForSite(siteDetail, smtpSiteIds);
  const family = siteFamily(siteDetail?.integration_type_name);
  // The site's servers (no cameras or tunnel subnets) for the address card.
  const localTargets = useSiteLocalTargets();
  const siteServers = localTargets.filter(
    (target) =>
      target.source !== "camera" &&
      target.source !== "tunnel_subnet" &&
      target.source !== "tunnel_client"
  );
  // What the connector dials; alarm receivers are where alerts go out, so
  // they don't decide whether the site is private or cloud-only.
  const connectorServers = siteServers.filter(
    (target) => target.source !== "alarm_receiver"
  );
  const sitePrivate = connectorServers.some((target) =>
    isPrivateAddress(target.address)
  );
  const siteCameras = useTroubleshootingStore((s) => s.siteCameras);
  const recorderCameraCounts = React.useMemo(
    () => camerasPerRecordingServer(siteCameras ?? []),
    [siteCameras]
  );
  // Cloud-pull sites (Eagle Eye, Star4Live, EZCloud, KVS, Ajax video),
  // OpenEye OWS, the Nx relay: the connector only talks to the vendor's
  // cloud, never to an address at the site.
  const cloudSite = siteKind === "cloud";
  const siteCloudOnly =
    cloudSite ||
    (connectorServers.length > 0 &&
      connectorServers.every((target) => target.source === "relay"));
  const siteNote = siteDetail ? endpointProfile(siteDetail).note : null;
  const pendingProbe = useTroubleshootingStore((s) => s.pendingProbe);

  // Picking a site in the scope bar fills the analyzer and the Credential
  // test from its saved settings (ENG-2058), once per site. A Check
  // reachability hand-off already fills them for its own site, so that
  // site counts as handled.
  const handledSiteId = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (pendingProbe?.siteId != null) {
      handledSiteId.current = pendingProbe.siteId;
      return;
    }
    if (scope !== "site" || !siteDetail || siteDetail.id !== siteId) return;
    if (handledSiteId.current === siteDetail.id) return;
    handledSiteId.current = siteDetail.id;
    const host = (
      primaryHost(siteDetail) ||
      siteDetail.server_ip ||
      siteDetail.management_server_ip ||
      ""
    ).trim();
    if (!host) return;
    requestProbe({
      host,
      ports: sitePorts(siteDetail),
      siteId: siteDetail.id,
      site: siteDetail,
    });
  }, [scope, siteId, siteDetail, pendingProbe, requestProbe]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <SiteConnectorPath />
      <ConnectivityAnalyzer onCopy={onCopy} />
      {/* The ONVIF/RTSP login test is for RTSP-family sites. Any other site
          picked gets the saved-login test when connector-tools can log in
          to it, else a line saying why (Jacob). */}
      {scope === "site" && siteDetail && family !== "rtsp-pull" ? (
        savedLoginSupported(siteDetail) ? (
          // VMS and vendor-cloud sites: log in with the saved login the way
          // the connector does, via connector-tools' camera-list job (ENG-2065).
          <SavedLoginTest site={siteDetail} />
        ) : (
          <Card testId="credential-test-not-applicable">
            <CardHeader
              title="Credential test"
              hint={`Not available for ${siteDetail.name}: we can't yet test the saved login of a ${FAMILY_LABEL[family]} site this way, and the camera ONVIF and RTSP login test doesn't apply to it.`}
            />
          </Card>
        )
      ) : (
        <>
          <CredentialTest onCopy={onCopy} />
          {/* RTSP-family sites that auto-add can log in to (milestone_rtsp,
              wisenet, hikvision, …) get the saved-login test as well: the
              camera test checks one device, this checks the site's saved
              login (Jacob, K-7). */}
          {scope === "site" &&
            siteDetail &&
            savedLoginSupported(siteDetail) && (
              <SavedLoginTest site={siteDetail} />
            )}
        </>
      )}
      <SiteToSiteCard onCopy={onCopy} />
      <DirectPathsCard onCopy={onCopy} />

      {tunnels.length > 0 && (
        <Card
          accent={TS_COLORS.wg}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.75,
            p: 2,
            flexWrap: "wrap",
          }}
        >
          <KindChip kind="wireguard" />
          <Box sx={{ flex: 1, ...MULISH, fontSize: 14 }}>
            <b>
              {tunnels.length} WireGuard tunnel{tunnels.length > 1 ? "s" : ""}{" "}
              across your groups.
            </b>{" "}
            <Hint>
              Handshake health and the gateway-side debugger are on the
              WireGuard tab here; adding or editing tunnels stays on the
              WireGuard page.
            </Hint>
          </Box>
          <TsButton
            variant="outline"
            size="sm"
            color={TS_COLORS.wg}
            onClick={() => {
              setWgSubTab("peer-health");
              // Keep the site: setTab alone resets scope (each tab has its own).
              openTabForSite("wireguard", scope === "site" ? siteId : null);
            }}
          >
            Open WireGuard tab
          </TsButton>
        </Card>
      )}

      {scope === "site" && (siteKind === "direct" || cloudSite) && siteDetail && (
        // Every address the connector dials for this site (from the
        // integration's profile), not just server_ip: a Milestone site's
        // management, event and recording servers, a DW site's relay, a
        // cloud site's vendor server, ….
        // A private address means a network we route to (usually IPsec),
        // not a port forward.
        <Card testId="site-addresses-card">
          <CardHeader
            leading={<KindChip kind={cloudSite ? "cloud" : "direct"} />}
            title={
              siteCloudOnly
                ? "Through the vendor's cloud"
                : sitePrivate
                ? "Private network (site-to-site VPN)"
                : "Direct connection"
            }
            hint={
              siteCloudOnly
                ? // With no address, the note is the card's body instead.
                  (connectorServers.length > 0 ? siteNote : null) ??
                  "This site's connector reaches it through the vendor's cloud, so the check is the vendor's cloud, not an address at the site."
                : sitePrivate
                ? "We reach this site's servers on private addresses, usually over an IPsec site-to-site VPN, not a port forward."
                : "No VPN. We reach the VMS or cameras on a public address you forward to us."
            }
          />
          <Box sx={{ overflowX: "auto" }}>
            <ResponsiveTable>
              <TableHead>
                <TableRow>
                  {["What", "Address", "Ports", "Reachable", ""].map((h) => (
                    <TableCell key={h} sx={HEAD_CELL_SX}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {connectorServers.length === 0 && cloudSite && (
                  // Nothing at the site to dial: say why, not that an
                  // address is missing.
                  <TableRow>
                    <TableCell
                      sx={CELL_SX}
                      colSpan={5}
                      data-testid="site-addresses-cloud-note"
                    >
                      <Hint>
                        {siteNote ??
                          "This site streams through the vendor's cloud, so there is no address at the site to test from here."}
                      </Hint>
                    </TableCell>
                  </TableRow>
                )}
                {connectorServers.length === 0 && !cloudSite && (
                  <TableRow>
                    <TableCell sx={CELL_SX} colSpan={5}>
                      <Hint>
                        No server address is saved for{" "}
                        <SiteLink siteId={siteDetail.id}>
                          {siteDetail.name}
                        </SiteLink>
                        . Check its connection settings on the site page.
                      </Hint>
                    </TableCell>
                  </TableRow>
                )}
                {siteServers.map((target) => (
                  <TableRow key={`${target.source}-${target.address}`}>
                    <TableCell sx={CELL_SX}>
                      {SERVER_LABEL[target.source] ?? target.label}
                      {target.label &&
                      target.label !== SERVER_LABEL[target.source] ? (
                        <Box>
                          <Hint>{target.label}</Hint>
                        </Box>
                      ) : null}
                      {target.source === "recording_server" &&
                      recorderCameraCounts.has(target.address) ? (
                        <Box data-testid="recording-server-cameras">
                          <Hint>
                            {recorderCameraCounts.get(target.address)} camera
                            {recorderCameraCounts.get(target.address) === 1
                              ? ""
                              : "s"}
                          </Hint>
                        </Box>
                      ) : null}
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      <Mono>{target.address}</Mono>
                    </TableCell>
                    <TableCell
                      sx={{ ...CELL_SX, fontVariantNumeric: "tabular-nums" }}
                    >
                      {target.ports.length ? target.ports.join(", ") : "—"}
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      <Hint>Run the analyzer above to check</Hint>
                    </TableCell>
                    <TableCell sx={CELL_SX}>
                      <TsButton
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          requestProbe({
                            host: target.address,
                            ports: target.ports,
                            siteId: siteDetail.id,
                            site: siteDetail,
                          })
                        }
                      >
                        Probe this address
                      </TsButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </ResponsiveTable>
          </Box>
        </Card>
      )}

      {smtpCount > 0 && (
        <Card
          accent={TS_COLORS.smtp}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.75,
            p: 2,
            flexWrap: "wrap",
          }}
        >
          <KindChip kind="smtp" />
          <Box sx={{ flex: 1, ...MULISH, fontSize: 14 }}>
            <b>
              {smtpCount} site{smtpCount > 1 ? "s" : ""} send
              {smtpCount > 1 ? "" : "s"} images to us by email.
            </b>{" "}
            <Hint>
              No inbound connection. Delivery and camera matching are on the
              SMTP Cameras tab.
            </Hint>
          </Box>
          <TsButton
            variant="outline"
            size="sm"
            color={TS_COLORS.smtp}
            onClick={() =>
              openTabForSite("smtp-cameras", scope === "site" ? siteId : null)
            }
          >
            Open SMTP Cameras tab
          </TsButton>
        </Card>
      )}

      {scope === "site" && !siteDetailLoading && siteKind === "unknown" && (
        <EmptyCard>
          We could not tell how this site connects from its settings (no
          WireGuard tunnel, server address or SMTP integration recorded).
        </EmptyCard>
      )}
    </Box>
  );
};
